import { useEffect, useState } from "react";
import { Radar, ShieldCheck, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { formatArDate } from "@/lib/formatDate";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const HEALTH_META = {
  healthy:  { label: "سليم",   Icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  warning:  { label: "تحذير",  Icon: AlertTriangle, cls: "border-amber-200 bg-amber-50 text-amber-700"    },
  critical: { label: "حرجة",  Icon: XCircle,       cls: "border-red-200 bg-red-50 text-red-700"           },
};

const HealthPill = ({ v }) => {
  const m = HEALTH_META[v] || HEALTH_META.warning;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${m.cls}`}>
      <Icon className="h-3 w-3" strokeWidth={2} /> {m.label}
    </span>
  );
};

export default function AdminCoveragePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/admin/coverage`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        setRows(d.markets || []);
      } catch (e) { setErr(String(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const bucket = (v) => rows.filter((r) => r.coverage_health === v).length;

  return (
    <AdminLayout>
      <section className="space-y-6" data-testid="admin-coverage">
        <header className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
            Control Center · Market Coverage
          </p>
          <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            تغطية الأسواق
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
            صحّة تغطية كل سوق مُفعَّل — عدد المصادر النشطة، الموثوقة، تركيز المصادر، وحداثة الجلب.
            جميع المقاييس محسوبة من قاعدة البيانات مباشرةً بدون أي استدعاء LLM.
          </p>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: "healthy",  label: "أسواق سليمة",  value: bucket("healthy"),  cls: "text-emerald-700" },
            { key: "warning",  label: "أسواق بتحذير", value: bucket("warning"),  cls: "text-amber-700"   },
            { key: "critical", label: "أسواق حرجة",   value: bucket("critical"), cls: "text-red-700"     },
          ].map((c) => (
            <div key={c.key} className="rounded-xl border border-surface-200 bg-white p-4 shadow-card">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">{c.label}</p>
              <p dir="ltr" className={`mt-2 font-mono text-2xl font-bold tabular-nums ${c.cls}`} data-testid={`coverage-bucket-${c.key}`}>
                {c.value}
              </p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="rounded-xl border border-dashed border-surface-200 bg-white p-10 text-center text-sm text-ink-500">
            جارٍ التحميل…
          </div>
        ) : err ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">{err}</div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-surface-200 bg-white shadow-card">
            <table className="w-full text-right" data-testid="admin-coverage-table">
              <thead className="border-b border-surface-200 bg-surface-50 text-[10px] uppercase tracking-widest text-ink-500">
                <tr>
                  <th className="px-4 py-3 text-start font-semibold">السوق</th>
                  <th className="px-4 py-3 font-semibold">مصادر نشطة</th>
                  <th className="px-4 py-3 font-semibold">موثوقة</th>
                  <th className="px-4 py-3 font-semibold">أعمال</th>
                  <th className="px-4 py-3 font-semibold">إشارات اليوم</th>
                  <th className="px-4 py-3 font-semibold">٧ أيام</th>
                  <th className="px-4 py-3 font-semibold">٣٠ يوماً</th>
                  <th className="px-4 py-3 font-semibold">التركيز</th>
                  <th className="px-4 py-3 font-semibold">الحالة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100 text-sm">
                {rows.map((m) => {
                  const conc = m.source_concentration;
                  const concCls =
                    conc >= 0.8 ? "text-red-700" :
                    conc >= 0.5 ? "text-amber-700" : "text-ink-700";
                  return (
                    <tr key={m.country_iso2} data-testid={`coverage-row-${m.country_iso2}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink-900">{m.country_ar}</div>
                        <div className="font-mono text-[10px] text-ink-500">{m.country_iso2}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className="font-mono font-semibold text-ink-900" dir="ltr">{m.active_sources}</span>
                        <span className="text-ink-500"> / {m.total_sources}</span>
                      </td>
                      <td className="px-4 py-3">
                        {m.authoritative_active > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-fursa-700">
                            <ShieldCheck className="h-3 w-3" strokeWidth={2} />
                            <span dir="ltr">{m.authoritative_active}</span>
                          </span>
                        ) : (
                          <span className="text-xs text-ink-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" dir="ltr">{m.business_media_active}</td>
                      <td className="px-4 py-3 font-mono text-xs" dir="ltr">{m.signals_today}</td>
                      <td className="px-4 py-3 font-mono text-xs" dir="ltr">{m.signals_7d}</td>
                      <td className="px-4 py-3 font-mono text-xs" dir="ltr">{m.signals_30d}</td>
                      <td className="px-4 py-3">
                        <div className={`font-mono text-xs font-semibold ${concCls}`} dir="ltr">
                          {(conc * 100).toFixed(0)}%
                        </div>
                        {m.top_source && (
                          <div className="text-[10px] text-ink-500 line-clamp-1" title={m.top_source.name}>
                            {m.top_source.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3"><HealthPill v={m.coverage_health} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Rule legend — transparency */}
        <div className="rounded-xl border border-surface-200 bg-white p-4 text-[11px] text-ink-500 shadow-card">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ink-500">قواعد تصنيف الحالة (حتمية، بدون AI):</p>
          <ul className="space-y-1 leading-relaxed">
            <li><span className="font-semibold text-red-700">حرجة:</span> صفر مصادر موثوقة نشِطة.</li>
            <li><span className="font-semibold text-amber-700">تحذير:</span> أقل من مصدرَين نشطَين أو تركيز مصدر واحد يتجاوز ٨٠٪.</li>
            <li><span className="font-semibold text-emerald-700">سليم:</span> مصدران أو أكثر مع تغطية موثوقة وتركيز صحّي.</li>
          </ul>
        </div>
      </section>
    </AdminLayout>
  );
}
